import { Link } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Lightbulb,
  Rocket,
  Target,
  Users,
  TrendingUp,
  Shield,
  BarChart3,
  Zap,
  HelpCircle,
} from 'lucide-react';
import { useState } from 'react';

import SEO from '../components/SEO';

const GeschaeftsideenPage = () => (
  <div className="pt-20">
    <SEO
      title="Geschäftsideen & Start-Up Umsetzung mit KI – MVP-Entwicklung"
      description="Von der Idee zum marktreifen Produkt: MVP-Entwicklung, Ideenvalidierung und Go-to-Market mit KI. Start-Up Ideen umsetzen mit LAIzyones Magdeburg & Braunschweig."
      path="/geschaeftsideen"
      keywords="Geschäftsidee umsetzen, Start-Up Umsetzung KI, MVP Entwicklung, Ideenvalidierung, KI Start-Up, Gründerberatung KI, Produkt entwickeln lassen, Business Idee realisieren"
      breadcrumbs={[
        { name: 'Startseite', url: '/' },
        { name: 'Geschäftsideen & Start-Up Umsetzung' },
      ]}
      serviceSchema={{
        name: 'Geschäftsideen & Start-Up Umsetzung mit KI',
        description: 'Von der Idee zum marktreifen Produkt: Ideenvalidierung, MVP-Entwicklung und Go-to-Market mit KI-Technologie.',
      }}
      faqSchema={[
        { question: 'Was kostet die Entwicklung eines MVPs?', answer: 'Ein typisches KI-gestütztes MVP startet ab ca. 5.000 Euro, abhängig von Umfang und Komplexität. Im kostenlosen Erstgespräch erstellen wir ein transparentes Angebot.' },
        { question: 'Wie lange dauert es vom Konzept bis zum fertigen MVP?', answer: 'In der Regel 4–8 Wochen. Einfachere Produkte können schneller umgesetzt werden, komplexere Projekte benötigen entsprechend mehr Zeit.' },
        { question: 'Muss ich selbst programmieren können?', answer: 'Nein. Wir übernehmen die komplette technische Umsetzung. Sie bringen die Idee und das Branchenwissen ein — wir kümmern uns um Entwicklung, Design und KI-Integration.' },
        { question: 'Bekomme ich den Quellcode?', answer: 'Ja. Sie erhalten den vollständigen Quellcode Ihres Produkts. Es gibt keine Abhängigkeit — Sie können jederzeit selbst weiterentwickeln oder einen anderen Dienstleister beauftragen.' },
      ]}
    />
    <HeroSection />
    <WhatWeOfferSection />
    <ProcessSection />
    <WhyWithUsSection />
    <FAQSection />
    <CTASection />
  </div>
);

const HeroSection = () => (
  <section className="section bg-gray-50 relative overflow-hidden">
    <div className="absolute inset-0 grid-pattern" />
    <div className="container-custom relative z-10">
      <div className="max-w-4xl mx-auto text-center">
        <div className="badge mb-6 fade-up">Geschäftsideen & Start-Ups</div>
        <div className="reveal-container">
          <h1 className="heading-xl text-4xl md:text-5xl lg:text-6xl text-gray-900 reveal-text mb-6">
            Geschäftsideen <span className="gradient-text">entwickeln & umsetzen</span> mit KI
          </h1>
        </div>
        <p className="body-text text-base md:text-lg text-gray-600 max-w-3xl mx-auto fade-up mb-8">
          Von der ersten Idee bis zum marktreifen Produkt — wir unterstützen Gründer und
          Unternehmen dabei, Start-Up Ideen mit KI schnell und kostengünstig umzusetzen.
          Aus Magdeburg und Braunschweig, für Visionäre deutschlandweit.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center fade-up">
          <Link to="/#kontakt" className="btn-primary text-base">
            <span>Idee besprechen</span>
            <ArrowRight className="w-5 h-5 relative z-10" />
          </Link>
          <Link to="/ki-tool-entwicklung" className="btn-secondary text-base inline-flex items-center justify-center gap-2">
            KI-Tool-Entwicklung
          </Link>
        </div>
      </div>
    </div>
  </section>
);

const WhatWeOfferSection = () => {
  const offers = [
    { icon: Lightbulb, title: 'Ideenvalidierung', desc: 'Wir prüfen Ihre Geschäftsidee auf Marktpotenzial, technische Machbarkeit und Wirtschaftlichkeit — bevor Sie investieren.' },
    { icon: Target, title: 'Business-Modell-Entwicklung', desc: 'Gemeinsam erarbeiten wir ein tragfähiges Geschäftsmodell mit klarer Zielgruppe, Wertversprechen und Monetarisierungsstrategie.' },
    { icon: Rocket, title: 'MVP-Entwicklung', desc: 'Wir bauen Ihr Minimum Viable Product (MVP) mit KI — schnell, kosteneffizient und marktreif. Testen statt planen.' },
    { icon: BarChart3, title: 'Marktanalyse & Strategie', desc: 'KI-gestützte Marktanalyse: Wettbewerber, Trends und Zielgruppen identifizieren für eine fundierte Go-to-Market-Strategie.' },
    { icon: TrendingUp, title: 'Skalierung', desc: 'Wenn Ihr MVP Traktion gewinnt, helfen wir beim Skalieren: Infrastruktur, Automatisierung und Weiterentwicklung.' },
    { icon: Users, title: 'Gründerberatung', desc: 'Praxisnahe Beratung zu Finanzierung, Fördermitteln und Unternehmensaufbau — von Gründern für Gründer.' },
  ];

  return (
    <section className="section bg-white">
      <div className="container-custom">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="badge mb-4 fade-up">Unser Angebot</div>
          <h2 className="heading-lg text-3xl md:text-4xl text-gray-900 mb-4 fade-up">
            Vom Konzept zum <span className="gradient-text">fertigen Produkt</span>
          </h2>
          <p className="body-text text-base md:text-lg text-gray-600 fade-up">
            Wir begleiten Sie auf dem gesamten Weg — von der Idee über das MVP bis zur Skalierung.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {offers.map((o, i) => {
            const Icon = o.icon;
            return (
              <div key={i} className="card group animate-card">
                <div className="w-14 h-14 bg-gradient-to-br from-emerald-100 to-cyan-100 rounded-xl flex items-center justify-center mb-5 group-hover:from-emerald-200 group-hover:to-cyan-200 transition-colors">
                  <Icon className="w-7 h-7 text-emerald-600" />
                </div>
                <h3 className="font-['Space_Grotesk'] font-semibold text-lg text-gray-900 mb-2">{o.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{o.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const ProcessSection = () => {
  const steps = [
    { num: '01', title: 'Ideengespräch', desc: 'Wir besprechen Ihre Vision, analysieren den Markt und validieren die technische Machbarkeit.' },
    { num: '02', title: 'Konzept & Planung', desc: 'Business Model Canvas, Feature-Priorisierung und technischer Umsetzungsplan für Ihr MVP.' },
    { num: '03', title: 'MVP-Entwicklung', desc: 'Schnelle Umsetzung des Kernprodukts mit KI-Funktionen — marktreif in wenigen Wochen.' },
    { num: '04', title: 'Launch & Wachstum', desc: 'Go-to-Market-Begleitung, erste Nutzer gewinnen und basierend auf Feedback iterieren.' },
  ];

  return (
    <section className="section bg-gray-50">
      <div className="container-custom">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="badge mb-4 fade-up">Unser Vorgehen</div>
          <h2 className="heading-lg text-3xl md:text-4xl text-gray-900 mb-4 fade-up">
            So bringen wir Ihre <span className="gradient-text">Idee zum Fliegen</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((s, i) => (
            <div key={i} className="card text-center animate-card relative">
              <span className="font-['Space_Grotesk'] font-bold text-3xl gradient-text mb-3 block">{s.num}</span>
              <h3 className="font-['Space_Grotesk'] font-semibold text-base text-gray-900 mb-2">{s.title}</h3>
              <p className="text-gray-600 text-xs leading-relaxed">{s.desc}</p>
              {i < steps.length - 1 && (
                <div className="hidden lg:flex absolute top-1/2 -right-3 -translate-y-1/2 z-10 text-emerald-300">
                  <ChevronRight className="w-5 h-5" />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const WhyWithUsSection = () => {
  const reasons = [
    'Erfahrung mit KI-Produkten von der Idee bis zum Launch',
    'Schnelle MVP-Entwicklung in 4–8 Wochen',
    'Transparente Kostenstruktur ohne versteckte Gebühren',
    'DSGVO-konforme Entwicklung und Hosting in Deutschland',
    'Langfristige Betreuung auch nach dem Launch',
    'Netzwerk zu Investoren und Förderprogrammen',
  ];

  return (
    <section className="section bg-white">
      <div className="container-custom">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="badge mb-4 fade-up">Warum LAIzyones</div>
            <h2 className="heading-lg text-3xl md:text-4xl text-gray-900 mb-6 fade-up">
              Der richtige Partner für <span className="gradient-text">Ihre Gründung</span>
            </h2>
            <p className="text-gray-600 text-base leading-relaxed mb-8 fade-up">
              Als Gründer wissen wir, worauf es ankommt: Geschwindigkeit, Qualität und
              ein verlässlicher Partner, der mitdenkt. Start-Up Ideen umsetzen mit KI
              ist unsere Leidenschaft.
            </p>
            <div className="space-y-3 fade-up">
              {reasons.map((r, i) => (
                <div key={i} className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                  <span className="text-gray-700 font-medium text-sm">{r}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="fade-up">
            <div className="tech-card p-8">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 border border-emerald-500/20 mb-4">
                  <Rocket className="w-8 h-8 text-emerald-400" />
                </div>
                <h3 className="font-['Space_Grotesk'] font-bold text-2xl text-white mb-2">Von Gründern für Gründer</h3>
              </div>
              <p className="text-gray-400 text-center text-base leading-relaxed">
                Wir kennen die Herausforderungen einer Gründung aus eigener Erfahrung.
                LAIzyones selbst ist ein Start-Up — <span className="text-emerald-400 font-semibold">wir leben, was wir beraten</span>.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const faqs = [
    { q: 'Was kostet die Entwicklung eines MVPs?', a: 'Ein typisches KI-gestütztes MVP startet ab ca. 5.000 Euro, abhängig von Umfang und Komplexität. Im kostenlosen Erstgespräch erstellen wir ein transparentes Angebot für Ihr Projekt.' },
    { q: 'Wie lange dauert es vom Konzept bis zum fertigen MVP?', a: 'In der Regel 4–8 Wochen. Einfachere Produkte können schneller umgesetzt werden, komplexere Projekte benötigen entsprechend mehr Zeit.' },
    { q: 'Muss ich selbst programmieren können?', a: 'Nein. Wir übernehmen die komplette technische Umsetzung. Sie bringen die Idee und das Branchenwissen ein — wir kümmern uns um Entwicklung, Design und KI-Integration.' },
    { q: 'Bekomme ich den Quellcode?', a: 'Ja. Sie erhalten den vollständigen Quellcode Ihres Produkts. Es gibt keine Abhängigkeit von uns — Sie können jederzeit selbst weiterentwickeln oder einen anderen Dienstleister beauftragen.' },
  ];

  return (
    <section className="section bg-gray-50">
      <div className="container-custom">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="badge mb-4 fade-up"><HelpCircle className="w-3.5 h-3.5" /> Häufige Fragen</div>
          <h2 className="heading-lg text-3xl md:text-4xl text-gray-900 mb-4 fade-up">
            FAQ zu <span className="gradient-text">Geschäftsideen & Start-Ups</span>
          </h2>
        </div>
        <div className="max-w-3xl mx-auto space-y-3">
          {faqs.map((faq, i) => (
            <div key={i} className="card overflow-hidden">
              <button onClick={() => setOpenIndex(openIndex === i ? null : i)} className="w-full flex items-center justify-between p-5 text-left group">
                <h3 className="font-['Space_Grotesk'] font-semibold text-base text-gray-900 pr-4 group-hover:text-emerald-600 transition-colors">{faq.q}</h3>
                <ChevronDown className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform duration-300 ${openIndex === i ? 'rotate-180 text-emerald-500' : ''}`} />
              </button>
              <div className={`overflow-hidden transition-all duration-300 ${openIndex === i ? 'max-h-60 opacity-100' : 'max-h-0 opacity-0'}`}>
                <p className="px-5 pb-5 text-gray-600 text-sm leading-relaxed">{faq.a}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const CTASection = () => (
  <section className="section bg-white pb-24 md:pb-32">
    <div className="container-custom">
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-600 to-cyan-600 p-8 md:p-12 lg:p-16">
        <div className="absolute inset-0 grid-pattern opacity-10" />
        <div className="relative z-10 text-center max-w-3xl mx-auto">
          <h2 className="font-['Space_Grotesk'] font-bold text-3xl md:text-4xl text-white mb-6">
            Haben Sie eine Geschäftsidee?
          </h2>
          <p className="text-emerald-100 text-base md:text-lg mb-8">
            Lassen Sie uns in einem kostenlosen Gespräch herausfinden, wie wir Ihre Idee
            mit KI-Technologie zum Leben erwecken können.
          </p>
          <Link to="/#kontakt" className="inline-flex items-center justify-center gap-2 bg-white text-emerald-600 font-semibold px-8 py-4 rounded-xl hover:bg-emerald-50 transition-colors">
            Kostenloses Ideengespräch <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    </div>
  </section>
);

export default GeschaeftsideenPage;
